import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';

/**
 * The app's dropdown/context menu.
 *
 * Every menu is viewport-positioned (`position: fixed`) from a measured anchor,
 * so nothing depends on a `position: relative` parent and nothing is clipped by
 * a scrolling sidebar. That replaces the old `.mb-menu-list` approach, where an
 * inherited `right: 0` combined with a JS-set `left` stretched the box across
 * the whole viewport.
 *
 * Dismissal covers every input: click outside (via a backdrop, which also works
 * on touch, unlike the `onMouseLeave` this used to rely on), Escape, scroll and
 * resize. Focus moves into the menu on open, ↑/↓ roves between items, and focus
 * returns to the trigger on close.
 */

/** Where the menu should appear, in viewport coordinates. */
export interface MenuAnchor {
  x: number;
  y: number;
  /**
   * `end` right-aligns the menu's trailing edge to `x` (dropdowns hanging off a
   * button's right edge); `start` left-aligns it (context menus at a pointer).
   */
  align?: 'start' | 'end';
}

/** Anchor a menu below an element, right-aligned to it — the dropdown default. */
export function anchorBelow(el: HTMLElement): MenuAnchor {
  const r = el.getBoundingClientRect();
  return { x: r.right, y: r.bottom, align: 'end' };
}

/** Anchor a menu at the pointer — the context-menu default. */
export function anchorAtPointer(e: { clientX: number; clientY: number }): MenuAnchor {
  return { x: e.clientX, y: e.clientY, align: 'start' };
}

const MARGIN = 8;

/** Lets `MenuItem` dismiss the menu without every call site repeating it. */
const MenuCloseContext = createContext<() => void>(() => {});

export interface MenuProps {
  anchor: MenuAnchor;
  onClose: () => void;
  /** Accessible name, since a menu has no visible heading. */
  label: string;
  className?: string;
  children: ReactNode;
}

export function Menu({ anchor, onClose, label, className, children }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Captured on mount so focus can go back where it came from on close.
  const returnFocusTo = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement) : null,
  );

  // Measure, then clamp into the viewport: flip past the anchor when the menu
  // would overflow the right edge, slide up when it would overflow the bottom.
  // Runs before paint, so the menu is never visible at the wrong position.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    let left = anchor.align === 'end' ? anchor.x - width : anchor.x;
    if (left + width > window.innerWidth - MARGIN) left = anchor.x - width;
    if (left < MARGIN) left = MARGIN;
    let top = anchor.y + 4;
    if (top + height > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, window.innerHeight - MARGIN - height);
    }
    setPos({ top, left });
  }, [anchor]);

  // Focus only once a position exists: until then the menu is still
  // `visibility: hidden` (to avoid a flash at the unclamped spot), and a hidden
  // element cannot take focus — `.focus()` there is silently a no-op.
  useEffect(() => {
    if (!pos) return;
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [pos]);

  const close = useCallback(() => {
    returnFocusTo.current?.focus?.();
    onClose();
  }, [onClose]);

  // Escape dismisses; scrolling or resizing invalidates the measured anchor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    const onMove = () => close();
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [close]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'),
    );
    if (items.length === 0) return;
    if (e.key === 'Home') return items[0]!.focus();
    if (e.key === 'End') return items[items.length - 1]!.focus();
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
    items[(next + items.length) % items.length]!.focus();
  };

  return (
    <MenuCloseContext.Provider value={close}>
      {/* Backdrop, not `onMouseLeave`: dismissal has to work on touch too. */}
      <div className="mb-menu-backdrop" onClick={close} onContextMenu={close} />
      <div
        ref={ref}
        className={className ? `mb-menu-list ${className}` : 'mb-menu-list'}
        role="menu"
        aria-label={label}
        onKeyDown={onKeyDown}
        style={{
          top: pos?.top ?? anchor.y + 4,
          left: pos?.left ?? anchor.x,
          visibility: pos ? undefined : 'hidden',
        }}
      >
        {children}
      </div>
    </MenuCloseContext.Provider>
  );
}

/**
 * A menu entry. Selecting one always dismisses the menu first (which hands
 * focus back to the trigger), then runs the action — so an action that opens a
 * dialog or an inline editor still ends up owning focus.
 */
export function MenuItem({
  onSelect,
  children,
  danger,
  disabled,
}: {
  onSelect: () => void;
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  const close = useContext(MenuCloseContext);
  return (
    <button
      role="menuitem"
      disabled={disabled}
      className={danger ? 'mb-menu-danger' : undefined}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="mb-menu-sep" role="separator" />;
}
